/**
 * Graduated Volume Pricing
 *
 * Pricing tiers (per active user per month):
 *   1–50   users → $5/user
 *   51–150 users → $4/user
 *   151–300 users → $3/user
 *   301–500 users → $2/user
 *   501+   users → $1/user
 *
 * "Active user" = a user with 3+ turns in at least one conversation
 * during the billing period.
 *
 * The price is graduated (like tax brackets): the first 50 users are
 * always $5 each, the next 100 are $4 each, etc.
 */

export interface PricingTier {
  min: number;
  max: number | null;   // null = unlimited
  rate: number;          // $ per user / month
  label: string;
}

export const PRICING_TIERS: PricingTier[] = [
  { min: 1,   max: 50,   rate: 5, label: '1–50' },
  { min: 51,  max: 150,  rate: 4, label: '51–150' },
  { min: 151, max: 300,  rate: 3, label: '151–300' },
  { min: 301, max: 500,  rate: 2, label: '301–500' },
  { min: 501, max: null,  rate: 1, label: '501+' },
];

export const ACTIVE_USER_TURN_THRESHOLD = 3;

export interface PricingBreakdown {
  /** Total monthly cost in dollars (before discounts) */
  totalCost: number;
  /** Effective per-user cost (blended rate, before discounts) */
  effectiveRate: number;
  /** Number of users billed */
  users: number;
  /** Detail per tier band */
  bands: Array<{
    tier: PricingTier;
    usersInBand: number;
    bandCost: number;
  }>;
  /** Discounts applied */
  discounts: Array<{
    label: string;
    type: 'percentage' | 'fixed_amount';
    value: number;
    saving: number;
  }>;
  /** Total after all discounts */
  finalCost: number;
  /** Total discount amount */
  totalDiscount: number;
}

export interface DiscountInput {
  label: string;
  type: 'percentage' | 'fixed_amount';
  value: number;  // e.g. 20 for 20% or 50 for $50
}

export interface VolumeDiscountBand {
  min: number;
  max: number | null;
  percentOff: number;
  label?: string;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatTierLabel(min: number, max: number | null): string {
  return max === null ? `${min}+` : `${min}-${max}`;
}

/**
 * Apply percentage-off-RRP discount bands to pricing tiers.
 *
 * Bands are interpreted against the RRP tier boundaries, and the highest
 * applicable discount wins for any overlapping user range.
 */
export function applyVolumeDiscountBands(
  tiers: PricingTier[],
  discountBands: VolumeDiscountBand[] = []
): PricingTier[] {
  if (tiers.length === 0 || discountBands.length === 0) {
    return tiers.map((tier) => ({ ...tier }));
  }

  const normalizedBands = discountBands
    .filter((band) => band.percentOff > 0)
    .sort((left, right) => left.min - right.min);

  if (normalizedBands.length === 0) {
    return tiers.map((tier) => ({ ...tier }));
  }

  const discountedTiers: PricingTier[] = [];

  for (const tier of tiers) {
    const tierEndExclusive = tier.max === null ? null : tier.max + 1;
    const boundaries = new Set<number>([tier.min]);

    if (tierEndExclusive !== null) {
      boundaries.add(tierEndExclusive);
    }

    for (const band of normalizedBands) {
      const bandEndExclusive = band.max === null ? null : band.max + 1;
      const overlapsTier =
        (band.max === null || band.max >= tier.min) &&
        (tier.max === null || band.min <= tier.max);

      if (!overlapsTier) {
        continue;
      }

      if (band.min > tier.min && (tier.max === null || band.min <= tier.max)) {
        boundaries.add(band.min);
      }

      if (
        bandEndExclusive !== null &&
        bandEndExclusive > tier.min &&
        (tierEndExclusive === null || bandEndExclusive < tierEndExclusive)
      ) {
        boundaries.add(bandEndExclusive);
      }
    }

    const sortedBoundaries = Array.from(boundaries).sort((left, right) => left - right);

    if (tier.max === null) {
      const openEndedDiscountCandidates = normalizedBands
        .filter((band) => band.max === null && band.min <= sortedBoundaries[sortedBoundaries.length - 1])
        .sort((left, right) => left.min - right.min);
      const openEndedDiscount = openEndedDiscountCandidates[openEndedDiscountCandidates.length - 1];

      if (openEndedDiscount) {
        const lastBoundary = sortedBoundaries[sortedBoundaries.length - 1];
        if (lastBoundary < openEndedDiscount.min) {
          sortedBoundaries.push(openEndedDiscount.min);
        }
      }
    }

    for (let index = 0; index < sortedBoundaries.length; index += 1) {
      const segmentMin = sortedBoundaries[index];
      const nextBoundary = sortedBoundaries[index + 1] ?? null;
      const segmentMax = nextBoundary === null ? tier.max : nextBoundary - 1;

      if (segmentMax !== null && segmentMax < segmentMin) {
        continue;
      }

      const bestDiscount = normalizedBands.reduce((currentBest, band) => {
        const bandCoversSegment =
          band.min <= segmentMin &&
          (band.max === null || (segmentMax !== null && band.max >= segmentMax) || (segmentMax === null && band.max === null));

        if (!bandCoversSegment) {
          return currentBest;
        }

        return band.percentOff > currentBest ? band.percentOff : currentBest;
      }, 0);

      const discountedRate = bestDiscount > 0
        ? roundCurrency(tier.rate * (1 - bestDiscount / 100))
        : tier.rate;

      discountedTiers.push({
        min: segmentMin,
        max: segmentMax,
        rate: discountedRate,
        label: formatTierLabel(segmentMin, segmentMax),
      });

      if (nextBoundary === null) {
        break;
      }
    }
  }

  return discountedTiers;
}

/**
 * Calculate graduated pricing for a given number of active users,
 * optionally applying discounts (group, promotion, or code).
 */
export function calculatePricing(activeUsers: number, discounts?: DiscountInput[]): PricingBreakdown {
  return calculatePricingWithTiers(activeUsers, PRICING_TIERS, discounts);
}

/**
 * Calculate graduated pricing using caller-provided pricing tiers.
 */
export function calculatePricingWithTiers(
  activeUsers: number,
  tiers: PricingTier[],
  discounts?: DiscountInput[]
): PricingBreakdown {
  const bands: PricingBreakdown['bands'] = [];
  let remaining = Math.max(0, activeUsers);
  let totalCost = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;

    const bandSize = tier.max !== null ? tier.max - tier.min + 1 : remaining;
    const usersInBand = Math.min(remaining, bandSize);
    const bandCost = usersInBand * tier.rate;

    bands.push({ tier, usersInBand, bandCost });
    totalCost += bandCost;
    remaining -= usersInBand;
  }

  // Apply discounts sequentially
  const appliedDiscounts: PricingBreakdown['discounts'] = [];
  let runningCost = totalCost;

  if (discounts) {
    for (const d of discounts) {
      let saving = 0;
      if (d.type === 'percentage') {
        saving = Math.round(runningCost * d.value / 100);
      } else {
        saving = Math.min(d.value, runningCost);
      }
      appliedDiscounts.push({ label: d.label, type: d.type, value: d.value, saving });
      runningCost = Math.max(0, runningCost - saving);
    }
  }

  const totalDiscount = totalCost - runningCost;

  return {
    totalCost,
    effectiveRate: activeUsers > 0 ? Math.round((totalCost / activeUsers) * 100) / 100 : 0,
    users: activeUsers,
    bands,
    discounts: appliedDiscounts,
    totalDiscount,
    finalCost: runningCost,
  };
}

/**
 * Format a dollar amount for display.
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Format effective rate for display.
 */
export function formatRate(rate: number): string {
  return `$${rate.toFixed(2)}`;
}

// ─── Billing Period Calculation ──────────────────────────────────────────────

export interface BillingPeriod {
  periodNumber: number;  // 1-based
  start: Date;
  end: Date;             // inclusive last day of period
}

/**
 * Calculate billing periods anchored to a tenant's billing start date.
 * Each period runs from anchor day of month N to (anchor day - 1) of month N+1.
 * e.g., if anchor = May 21, period 1 = May 21 – Jun 20, period 2 = Jun 21 – Jul 20, etc.
 *
 * Handles month-end edge cases: if anchor is 31st and next month has 30 days,
 * the period end adjusts to the last day of that month.
 */
export function getBillingPeriods(anchorDate: Date, upToDate?: Date): BillingPeriod[] {
  const periods: BillingPeriod[] = [];
  const now = upToDate || new Date();
  const anchorDay = anchorDate.getUTCDate();

  let periodNumber = 1;
  let periodStart = new Date(Date.UTC(
    anchorDate.getUTCFullYear(),
    anchorDate.getUTCMonth(),
    anchorDay
  ));

  while (periodStart <= now) {
    // Period end = day before the next period start
    const nextStart = getNextPeriodStart(periodStart, anchorDay);
    const periodEnd = new Date(nextStart.getTime() - 86400000); // minus 1 day

    periods.push({
      periodNumber,
      start: new Date(periodStart),
      end: periodEnd,
    });

    periodStart = nextStart;
    periodNumber++;
  }

  return periods;
}

/**
 * Get the current billing period for a tenant.
 */
export function getCurrentBillingPeriod(anchorDate: Date): BillingPeriod {
  const periods = getBillingPeriods(anchorDate);
  return periods[periods.length - 1];
}

/**
 * Calculate the next period start by advancing one month from current start,
 * clamping to the anchor day (handling months with fewer days).
 */
function getNextPeriodStart(currentStart: Date, anchorDay: number): Date {
  const year = currentStart.getUTCFullYear();
  const month = currentStart.getUTCMonth() + 1; // next month

  // Get the last day of the next month
  const lastDayOfNextMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(anchorDay, lastDayOfNextMonth);

  return new Date(Date.UTC(
    month > 11 ? year + 1 : year,
    month > 11 ? 0 : month,
    day
  ));
}
